import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { Badge, Button, EmptyState, Field, Input, Section, Spinner, Switch } from '@zt/ui';
import { api } from '../../lib/api';
import { HelpTip, PageFrame } from '../../lib/help';
import { useI18n, useT } from '../../lib/i18n';
import { fieldError, useApiMutation } from '../../lib/mutation';
import { usePermissions } from '../../lib/session';
import { useStoreChoice } from '../../lib/store-choice';
import { StorePicker } from '../payments';

/**
 * Ürün önerileri (F9-02, ADR-127): "birlikte alınanlar", sepet önerisi ve yeniden alım zamanı. Yapay zekâ sağlayıcısı
 * kullanmaz; mağazanın gerçek siparişlerinden her gece hesaplanır. Veri yetersizse vitrinde bölüm görünmez.
 */

interface Settings {
  alsoBought: boolean;
  cart: boolean;
  replenishment: boolean;
  windowDays: number;
  minPairBuyers: number;
  minConfidenceBps: number;
  maxPerProduct: number;
  includeB2b: boolean;
}
interface Overview {
  storeName: string;
  settings: Settings;
  runs: Array<{
    id: string;
    trigger: 'schedule' | 'manual';
    status: 'ok' | 'error';
    windowDays: number;
    orders: number;
    products: number;
    pairsFound: number;
    pairsKept: number;
    durationMs: number | null;
    errorCode: string | null;
    startedAt: string;
    userName: string | null;
  }>;
  stats: { pairs: number; productsWithRecommendations: number };
  topPairs: Array<{
    productId: string;
    productTitle: string;
    relatedId: string;
    relatedTitle: string;
    pairOrders: number;
    pairBuyers: number;
    confidenceBps: number;
    liftMilli: number;
  }>;
}

/** Baz puan → "%44,4" (gösterim). */
function bpsPercent(bps: number, locale: string) {
  return new Intl.NumberFormat(locale === 'en' ? 'en-GB' : 'tr-TR', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(bps / 10_000);
}

export function RecommendationsPage() {
  const t = useT();
  const { locale, formatDate, formatNumber } = useI18n();
  const { can } = usePermissions();
  const canWrite = can('catalog.product.write');
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const q = useQuery({
    queryKey: ['recommendations', storeId],
    queryFn: () => api.get<Overview>(`/v1/admin/recommendations?storeId=${storeId}`),
    enabled: !!storeId,
  });
  const run = useApiMutation(
    () => api.post<Overview['runs'][number]>('/v1/admin/recommendations/run', { storeId }),
    {
      invalidate: [['recommendations']],
      success: (r) => t('ai.recommendations.ran', { orders: r.orders, pairs: r.pairsKept }),
    },
  );
  const o = q.data;
  return (
    <PageFrame helpKey="page.ai_recommendations">
      <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
      {!loading && !stores.length ? (
        <EmptyState
          title={t('ai.recommendations.noStore')}
          description={t('ai.recommendations.noStoreLead')}
        />
      ) : !o ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <Section
            title={t('ai.recommendations.statusTitle')}
            description={t('ai.recommendations.statusLead')}
            action={
              canWrite && (
                <span className="inline-flex items-center gap-1">
                  <Button size="sm" loading={run.isPending} onClick={() => run.mutate(undefined)}>
                    <RefreshCw className="h-4 w-4" aria-hidden />
                    {t('ai.recommendations.run')}
                  </Button>
                  <HelpTip k="recommendations.run" />
                </span>
              )
            }
          >
            <p className="text-[0.9375rem]">
              {t('ai.recommendations.stats', {
                pairs: formatNumber(o.stats.pairs),
                products: formatNumber(o.stats.productsWithRecommendations),
              })}
            </p>
            {o.runs.length ? (
              <div className="mt-4 overflow-x-auto rounded-md border border-line">
                <table className="w-full min-w-[40rem] text-sm">
                  <caption className="px-3 py-2 text-start font-medium">
                    {t('ai.recommendations.runsTitle')}
                  </caption>
                  <thead className="bg-surface-2 text-ink-2">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">{t('ai.recommendations.when')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('ai.recommendations.trigger')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.recommendations.orders')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.recommendations.pairsKept')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('ai.recommendations.result')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {o.runs.map((r) => (
                      <tr key={r.id}>
                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.startedAt)}</td>
                        <td className="px-3 py-2">
                          {r.trigger === 'manual'
                            ? t('ai.recommendations.manualBy', { name: r.userName ?? '—' })
                            : t('ai.recommendations.scheduled')}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">{formatNumber(r.orders)}</td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {formatNumber(r.pairsKept)} / {formatNumber(r.pairsFound)}
                        </td>
                        <td className="px-3 py-2">
                          <Badge tone={r.status === 'ok' ? 'moss' : 'rose'}>
                            {t(`ai.recommendations.status.${r.status}`)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-2 text-sm text-ink-3">{t('ai.recommendations.noRuns')}</p>
            )}
          </Section>

          {canWrite && <SettingsSection key={JSON.stringify(o.settings)} s={o.settings} />}

          <Section
            title={t('ai.recommendations.pairsTitle')}
            description={t('ai.recommendations.pairsLead')}
            action={<HelpTip k="recommendations.pairs" />}
          >
            {o.topPairs.length ? (
              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full min-w-[44rem] text-sm">
                  <caption className="sr-only">{t('ai.recommendations.pairsTitle')}</caption>
                  <thead className="bg-surface-2 text-ink-2">
                    <tr>
                      <th className="px-3 py-2 text-start font-medium">{t('ai.recommendations.product')}</th>
                      <th className="px-3 py-2 text-start font-medium">{t('ai.recommendations.related')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.recommendations.buyers')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.recommendations.confidence')}</th>
                      <th className="px-3 py-2 text-end font-medium">{t('ai.recommendations.lift')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {o.topPairs.map((p) => (
                      <tr key={`${p.productId}-${p.relatedId}`}>
                        <td className="px-3 py-2">{p.productTitle}</td>
                        <td className="px-3 py-2">{p.relatedTitle}</td>
                        <td className="px-3 py-2 text-end tabular-nums">{formatNumber(p.pairBuyers)}</td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          {bpsPercent(p.confidenceBps, locale)}
                        </td>
                        <td className="px-3 py-2 text-end tabular-nums">
                          ×{formatNumber(p.liftMilli / 1000, 2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <EmptyState
                title={t('ai.recommendations.noPairs')}
                description={t('ai.recommendations.noPairsLead')}
              />
            )}
          </Section>
        </>
      )}
    </PageFrame>
  );
}

function SettingsSection({ s }: { s: Settings }) {
  const t = useT();
  const [form, setForm] = useState({
    ...s,
    windowDays: String(s.windowDays),
    minPairBuyers: String(s.minPairBuyers),
    minConfidence: String(s.minConfidenceBps / 100),
    maxPerProduct: String(s.maxPerProduct),
  });
  const save = useApiMutation(
    (body: Record<string, unknown>) => api.put('/v1/admin/recommendations/settings', body),
    {
      invalidate: [['recommendations']],
      success: t('ai.recommendations.saved'),
    },
  );
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));
  const err = (f: string) => fieldError(save.error, f);
  const toggle = (k: 'alsoBought' | 'cart' | 'replenishment' | 'includeB2b') => (
    <div className="flex items-start gap-3 rounded-md border border-line px-4 py-3">
      <Switch
        checked={form[k]}
        onCheckedChange={(v) => set(k, v)}
        label={t(`ai.recommendations.toggles.${k}`)}
      />
      <div className="text-sm">
        <p className="font-medium">{t(`ai.recommendations.toggles.${k}`)}</p>
        <p className="text-ink-3">{t(`ai.recommendations.toggleHints.${k}`)}</p>
      </div>
    </div>
  );
  return (
    <Section title={t('ai.recommendations.settingsTitle')} action={<HelpTip k="recommendations.settings" />}>
      <div className="grid gap-3 sm:grid-cols-2">
        {toggle('alsoBought')}
        {toggle('cart')}
        {toggle('replenishment')}
        {toggle('includeB2b')}
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Field label={t('ai.recommendations.windowDays')} error={err('windowDays')}>
          {(p) => (
            <Input
              {...p}
              type="number"
              min={30}
              max={730}
              value={form.windowDays}
              onChange={(e) => set('windowDays', e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('ai.recommendations.minPairBuyers')}
          description={t('ai.recommendations.minPairBuyersHint')}
          error={err('minPairBuyers')}
        >
          {(p) => (
            <Input
              {...p}
              type="number"
              min={2}
              max={50}
              value={form.minPairBuyers}
              onChange={(e) => set('minPairBuyers', e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('ai.recommendations.minConfidence')}
          description={t('ai.recommendations.minConfidenceHint')}
          error={err('minConfidenceBps')}
        >
          {(p) => (
            <Input
              {...p}
              type="number"
              min={0}
              max={100}
              step="0.5"
              value={form.minConfidence}
              onChange={(e) => set('minConfidence', e.target.value)}
            />
          )}
        </Field>
        <Field label={t('ai.recommendations.maxPerProduct')} error={err('maxPerProduct')}>
          {(p) => (
            <Input
              {...p}
              type="number"
              min={1}
              max={20}
              value={form.maxPerProduct}
              onChange={(e) => set('maxPerProduct', e.target.value)}
            />
          )}
        </Field>
      </div>
      <div className="mt-4">
        <Button
          variant="primary"
          loading={save.isPending}
          onClick={() =>
            save.mutate({
              alsoBought: form.alsoBought,
              cart: form.cart,
              replenishment: form.replenishment,
              includeB2b: form.includeB2b,
              windowDays: Number(form.windowDays),
              minPairBuyers: Number(form.minPairBuyers),
              // Yüzde → baz puan (yarım yüzdeye kadar; tam sayı).
              minConfidenceBps: Math.round(Number(form.minConfidence.replace(',', '.')) * 100),
              maxPerProduct: Number(form.maxPerProduct),
            })
          }
        >
          {t('ai.recommendations.save')}
        </Button>
      </div>
    </Section>
  );
}
